import { Injectable, OnDestroy } from '@angular/core'
import { Capacitor, PluginListenerHandle } from '@capacitor/core'
import { Platform } from '@ionic/angular'
import { BehaviorSubject, Subject, takeUntil } from 'rxjs'
import CMPPlugin, { CookieVendorId } from './cookie-consent.fixtures'

interface ConsentMap {
  [vendorId: string]: boolean
}

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService implements OnDestroy {
  private _destroySubject$: Subject<void> = new Subject<void>()
  private consentChangeListener: PluginListenerHandle | undefined = undefined
  private queueConsentManager: boolean = false

  isConsentVisible: BehaviorSubject<boolean> = new BehaviorSubject(false)
  isPreferencesVisible: BehaviorSubject<boolean> = new BehaviorSubject(false)
  canTrack: BehaviorSubject<boolean> = new BehaviorSubject(false)

  userPreferences: BehaviorSubject<ConsentMap> = new BehaviorSubject(
    this.initialConsentMap()
  )

  private get cmp(): any {
    const w = window as any
    if (!w.__cmp) {
      w.__cmp = () => {} // no-op fallback
    }
    return w.__cmp
  }

  constructor(private platform: Platform) {
    this.setListeners()
    this.loadConsent()

    if (platform.is('ios')) {
      this.canTrack
        .pipe(takeUntil(this._destroySubject$))
        .subscribe((canTrack) => {
          if (this.queueConsentManager && canTrack) {
            this.showConsentManager(false)
            this.queueConsentManager = false
          }
        })
    }
  }

  ngOnDestroy() {
    this.removeListeners()
    this._destroySubject$.next()
    this._destroySubject$.complete()
  }

  private initialConsentMap(): ConsentMap {
    const initial: ConsentMap = {}
    for (const vendor of Object.values(CookieVendorId)) {
      initial[vendor] = false
    }
    return initial
  }

  async showConsentManager(forceOpen: boolean) {
    if (Capacitor.isNativePlatform()) {
      if (!this.canTrack.value) {
        this.loadAppleTrackingConsent()
        this.queueConsentManager = true
        return
      }
      await CMPPlugin.openConfig({ forceOpen })
    } else {
      this.cmp('showScreen')
    }
  }

  private async updateAllNativeConsent() {
    const consentMap: ConsentMap = {}
    for (const vendor of Object.values(CookieVendorId)) {
      const { hasConsent } = await CMPPlugin.hasConsent({ key: vendor })
      consentMap[vendor] = hasConsent
    }
    this.userPreferences.next(consentMap)
  }

  private async updateAllWebConsent(eventname: any, cmp: any, _: any) {
    if (eventname !== 'consent') {
      return
    }
    const cmpData = this.cmp('getCMPData')
    if (cmpData === undefined || cmpData.vendorConsents === undefined) {
      return
    }
    const consentMap: ConsentMap = {}
    for (const vendor of Object.values(CookieVendorId)) {
      // Prüfe explizit auf true, weil es manchmal auch undefined ist
      const hasConsent = cmpData.vendorConsents[vendor] === true
      consentMap[vendor] = hasConsent
    }
    this.userPreferences.next(consentMap)
  }

  private async setListeners() {
    if (Capacitor.isNativePlatform()) {
      this.consentChangeListener = await CMPPlugin.addListener(
        'onConsentChanged',
        async () => {
          await this.loadAppleTrackingConsent()
          await this.updateAllNativeConsent()
        }
      )
    } else {
      this.cmp(
        'addEventListener',
        ['consent', this.updateAllWebConsent.bind(this), false],
        null
      )
      this.updateAllWebConsent('consent', this.cmp, undefined).then()
    }
  }

  private removeListeners() {
    if (Capacitor.isNativePlatform()) {
      this.consentChangeListener?.remove()
    } else {
      this.cmp(
        'removeEventListener',
        ['consent', this.updateAllWebConsent.bind(this), false],
        null
      )
    }
  }

  private async loadConsent() {
    if (this.platform.is('ios')) {
      await this.loadAppleTrackingConsent()
    } else {
      this.canTrack.next(true)
    }
    if (Capacitor.isNativePlatform()) {
      await this.updateAllNativeConsent()
    }
  }

  private async loadAppleTrackingConsent() {
    const appleTracking = await CMPPlugin.hasAppleTrackingConsent()
    this.canTrack.next(appleTracking.hasConsent)
  }
}
